import GoBackMenu from '@/components/GoBackNavbar';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { CourseLessonContentTO } from '@/models/CourseLessonContentTO';
import { LanguageType } from '@/models/LanguageType';
import Tag from '@/models/Tag';
import { ApplicationState } from '@/store';
import { getCategoriesClear, getCategoriesRequest } from '@/store/category/action';
import { closeModal } from '@/store/common/actions';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import MyCreationTags from '../MyCreationTags/index';
import { FormObject, formSchema, initialFormObject, LanguageOptions, myCreationsConstants } from './FormConfig';
import './style.scss';

interface Props {
    setCourse: (course: CourseLessonContentTO) => void;
    setIsFormValid: (valid: boolean) => void;
    setIsFormDirty?: (dirty: boolean) => void;
    course: CourseLessonContentTO;
    title: string;
}

const MyListForm: React.FC<Props> = (props: Props) => {
    const { setCourse, setIsFormValid, course, title, setIsFormDirty } = props;
    const { strings, fetchedCategories, person } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        fetchedCategories: state.categories.data.categories,
        person: state.identity.data.person,
    }));

    const [newForm, setNewForm] = useState<FormObject>(initialFormObject);
    const [addCategory, setAddCategory] = useState<boolean>(false);
    const languageOptions: LanguageOptions[] = [
        { description: strings.LANGUAGE_PT, value: Constants.language.PT },
        { description: strings.LANGUAGE_EN, value: Constants.language.EN },
    ];
    const dispatch = useDispatch();

    const isEditing = Boolean(course && course.id);

    const hasFormChanged = (courseObj: CourseLessonContentTO | FormObject) => {
        courseObj.locale = courseObj.locale || strings._LANGUAGE;
        return !(
            _.isEqual(_.pick(course, 'name', 'description', 'locale'), _.omit(courseObj, ['categories', 'tags'])) &&
            course.categories.length === courseObj.categories.length &&
            course.categories.every((category: Category) =>
                courseObj.categories.find((initialCategory: Category) => category.id === initialCategory.id),
            ) &&
            course.tags.length === courseObj.tags.length &&
            course.tags.every((tag: Tag) => courseObj.tags.find((initialTag: Tag) => tag.id === initialTag.id))
        );
    };

    const isOwnFormDirty = hasFormChanged(isEditing ? newForm : initialFormObject);

    const handleCategories = (cat: string, index: number) => {
        const tmpArray: Category[] = [...newForm.categories];
        if (!!cat.trim()) {
            const catId = parseInt(cat, 10);
            const currentCategory = fetchedCategories && fetchedCategories.find((category: Category) => category.id === catId);
            if (currentCategory) {
                tmpArray[tmpArray.length > 0 ? index : 0] = currentCategory;
            }
        } else if (tmpArray.length > 1) {
            tmpArray.splice(index, 1);
        } else {
            tmpArray.pop();
        }
        setNewForm((prevState) => ({ ...prevState, categories: tmpArray }));
    };

    const handleRemoveCategory = () => {
        const tmpArray: Category[] = [...newForm.categories];
        if (tmpArray.length > 1) {
            tmpArray.pop();
            setNewForm((prevState) => ({ ...prevState, categories: tmpArray }));
        }

        setAddCategory(false);
    };

    const handleFilledStyle = (value: string | Tag[] | Category[] | undefined, field: string) => {
        let filledClassName: string = '';
        if (field.match(myCreationsConstants.categories) || field.match(myCreationsConstants.tags)) {
            const arrayValue = value as Tag[] | Category[];
            arrayValue.length > 0 && (filledClassName = 'filled');
        } else {
            value !== initialFormObject[field] && (filledClassName = 'filled');
        }
        return filledClassName;
    };

    const handleTags = (newTags: Tag[]) => {
        setNewForm((prevState) => ({ ...prevState, tags: newTags }));
    };

    const handleFormFields = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, field: string) => {
        e.persist();
        setNewForm((prevState) => ({ ...prevState, [field]: e.target.value }));
    };

    const renderCategoriesOptions = (categoryIndex: number) => {
        let newFetchedCategories: JSX.Element[] = [];
        if (fetchedCategories) {
            const nonCurrentIndex = newForm.categories.findIndex((cat: Category, index: number) => categoryIndex !== index);
            newFetchedCategories = fetchedCategories.reduce((acc: JSX.Element[], category: Category) => {
                if (!newForm.categories[nonCurrentIndex] || newForm.categories[nonCurrentIndex].id !== category.id) {
                    acc.push(
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>,
                    );
                }
                return acc;
            }, []);
        }
        return newFetchedCategories;
    };

    const handleCategorySelected = (index: number) => {
        if (newForm.categories && newForm.categories[index]) {
            return newForm.categories[index].id;
        }
        return '';
    };

    useEffect(() => {
        if (isEditing) {
            setNewForm({
                name: course.name,
                description: course.description,
                tags: course.tags,
                categories: course.categories,
                locale: course.locale,
            });
            setAddCategory(course.categories && course.categories.length > 1);
        }
    }, [course]);

    useEffect(() => {
        setCourse({
            ...course,
            locale: newForm.locale as LanguageType,
            name: newForm.name,
            description: newForm.description || '',
            tags: newForm.tags,
            categories: newForm.categories,
        });

        setIsFormDirty && setIsFormDirty(isOwnFormDirty);

        formSchema
            .validate(newForm)
            .then(() => setIsFormValid(true))
            .catch(() => setIsFormValid(false));
    }, [newForm]);

    useEffect(() => {
        if (!isEditing) {
            setNewForm({ ...newForm, locale: strings._LANGUAGE });
        }
    }, [strings._LANGUAGE]);

    useEffect(() => {
        const { orgId, applicationId } = person;
        dispatch(getCategoriesRequest({ orgId, applicationId }));
        return () => {
            getCategoriesClear();
        };
    }, [person]);

    const myContentIdToRedirectBack = localStorage.getItem(Constants.MY_CONTENT_ID_TO_REDIRECT_BACK);
    const disableLang = isEditing || myContentIdToRedirectBack !== null;

    return (
        <div className="mylist">
            <GoBackMenu
                confirmable={isOwnFormDirty}
                goBackPath={''}
                onClick={() => isOwnFormDirty && dispatch(closeModal(Constants.modal.MORE_OPTIONS))}
            />

            <div className="mylist-form">
                <h1 className="title"> {title}</h1>
                <div className="input-section">
                    <h1>
                        {strings.TITLE}
                        <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                    </h1>

                    <input
                        className={`form-control ${handleFilledStyle(newForm.name, myCreationsConstants.name)}`}
                        maxLength={255}
                        onChange={(e) => handleFormFields(e, myCreationsConstants.name)}
                        placeholder={strings.TYPE_THE_TITLE}
                        type="text"
                        value={newForm.name}
                    />
                </div>
                <div className="input-section">
                    <h1>{strings.DESCRIPTION}</h1>
                    <textarea
                        className={`form-control ${handleFilledStyle(newForm.description, myCreationsConstants.description)}`}
                        maxLength={4000}
                        onChange={(e) => handleFormFields(e, myCreationsConstants.description)}
                        placeholder={strings.SHORT_DESCRIPTION}
                        rows={3}
                        value={newForm.description}
                    />
                </div>
                <div className="input-section">
                    <h1>
                        {strings.SELECT_LANGUAGE}
                        <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                    </h1>
                    <select
                        className={`form-control ${handleFilledStyle(newForm.locale, myCreationsConstants.locale)} ${disableLang ? 'disabled' : ''}`}
                        disabled={disableLang}
                        name="language"
                        onChange={(e) => handleFormFields(e, myCreationsConstants.locale)}
                        value={newForm.locale}
                    >
                        {languageOptions.map((l: LanguageOptions, index: number) => (
                            <option defaultChecked={strings._LANGUAGE === l.value} key={index} value={l.value}>
                                {l.description}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="input-section">
                    <h1>
                        {strings.CATEGORY}
                        <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                    </h1>
                    <select
                        className={`form-control ${handleFilledStyle(newForm.categories, myCreationsConstants.categories)}`}
                        name="category1"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleCategories(e.target.value, 0)}
                        value={handleCategorySelected(0)}
                    >
                        <option value={''}>{strings.SELECT_CATEGORY}</option>

                        {renderCategoriesOptions(0)}
                    </select>
                    {!addCategory && (
                        <div className="d-flex justify-content-end add-category">
                            <span onClick={() => setAddCategory(true)} onKeyDown={() => {}} role="button" tabIndex={0}>
                                {strings.ADD_CATEGORY}
                            </span>
                        </div>
                    )}
                </div>
                {addCategory && (
                    <>
                        <div className="input-section">
                            <select
                                className={`form-control ${handleFilledStyle(newForm.categories, myCreationsConstants.categories)}`}
                                name="category2"
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleCategories(e.target.value, 1)}
                                value={handleCategorySelected(1)}
                            >
                                <option value={''}>{strings.SELECT_CATEGORY}</option>
                                {renderCategoriesOptions(1)}
                            </select>
                            <div className="d-flex justify-content-end delete-label">
                                <div
                                    className="inner-container"
                                    onClick={() => {
                                        handleRemoveCategory();
                                    }}
                                >
                                    <i className="material-icons-outlined" onKeyDown={() => {}} tabIndex={0}>
                                        {Constants.icons.DELETE}
                                    </i>
                                    <span onKeyDown={() => {}} tabIndex={0}>
                                        {strings.REMOVE}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div className="input-section">
                    <h1>
                        {strings.TAGS}
                        <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                    </h1>

                    <MyCreationTags
                        handleFill={handleFilledStyle(newForm.tags, myCreationsConstants.tags)}
                        setTags={handleTags}
                        tags={newForm.tags}
                    />
                </div>

                <div className="warning-input">
                    <p className="warning-text">
                        {Constants.symbol.ASTERISK}
                        {strings.REQUIRED_FIELDS}
                    </p>
                </div>
            </div>
        </div>
    );
};
export default MyListForm;
